// NodeMissionTaskStep.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { MISSION_TASK_HANDLED_BY } from 'missions/taskmodal/missionTaskModalUtils';

// CSS
import './NodeMissionTaskStep.css';

// Icons
import Icon from 'svg/Icon';

function resolveStepState(status) {
  const normalized = String(status || '').trim();
  if (normalized === 'complete') return 'complete';
  if (normalized === 'skipped') return 'skipped';
  if (normalized === 'blocked' || normalized === 'failed') return 'blocked';
  if (
    normalized === 'ready'
    || normalized === 'inProgress'
    || normalized === 'running'
  ) {
    return 'active';
  }
  return 'pending';
}

/**
 * NodeMissionTaskStep
 *
 * Wraps a task panel with a vertical process rail (numbered marker + connector).
 *
 * @param {ReactNode} children - Task panel content.
 * @param {boolean} [isLast=false] - Whether this is the last step in the list.
 * @param {number|null} stepNumber - 1-based human step number; null for bot markers.
 * @param {object|null} task - Workflow task (for status / handledBy).
 * @returns {ReactNode}
 */
const NodeMissionTaskStep = React.memo(React.forwardRef(({
  children,
  isLast = false,
  stepNumber = null,
  task = null,
}, ref) => {
  const { theme } = useTheme();
  const state = resolveStepState(task?.status);
  const isBot = task?.handledBy === MISSION_TASK_HANDLED_BY.BOT;
  const showNumber = Number.isFinite(Number(stepNumber)) && Number(stepNumber) > 0;

  const markerStyle = useMemo(() => {
    if (state === 'complete') {
      return {
        backgroundColor: theme.success,
        borderColor: theme.success,
        color: theme.highlightForeground || '#fff',
      };
    }
    if (state === 'skipped') {
      return {
        backgroundColor: 'transparent',
        borderColor: theme.outlineMuted,
        color: theme.foregroundMuted,
        opacity: 0.55,
      };
    }
    if (state === 'blocked') {
      return {
        backgroundColor: theme.backgroundMuted,
        borderColor: theme.warning,
        color: theme.warning,
      };
    }
    if (state === 'active') {
      return {
        backgroundColor: theme.backgroundMuted,
        borderColor: theme.outlineMuted,
        color: theme.foreground,
      };
    }
    return {
      backgroundColor: 'transparent',
      borderColor: theme.outlineMuted,
      color: theme.foregroundMuted,
    };
  }, [state, theme]);

  const connectorComplete = state === 'complete';

  return (
    <div
      ref={ref}
      className={`node-mission-task-step is-${state}`}
      data-mission-task-key={String(task?.key || '')}
    >
      <div className="node-mission-task-step-rail" aria-hidden="true">
        <div
          className={`node-mission-task-step-marker is-${state}${isBot ? ' is-bot' : ''}`}
          style={markerStyle}
        >
          {state === 'complete' ? (
            <Icon
              iconKey="check"
              width={20}
              height={20}
              color="currentColor"
            />
          ) : state === 'skipped' ? (
            <Icon
              iconKey="keyboard_double_arrow_right"
              width={18}
              height={18}
              color="currentColor"
            />
          ) : isBot ? (
            <Icon
              iconKey="robot_2"
              width={20}
              height={20}
              color="currentColor"
            />
          ) : showNumber ? (
            <span className="node-mission-task-step-number">{stepNumber}</span>
          ) : (
            <span className="node-mission-task-step-dot" />
          )}
        </div>

        {!isLast ? (
          <div
            className={`node-mission-task-step-connector${connectorComplete ? ' is-complete' : ''}`}
            style={{
              backgroundColor: connectorComplete ? theme.success : theme.outlineMuted,
            }}
          />
        ) : null}
      </div>

      <div className="node-mission-task-step-body">
        {children}
      </div>
    </div>
  );
}));

NodeMissionTaskStep.displayName = 'NodeMissionTaskStep';

export default NodeMissionTaskStep;
